import { Injectable, Provider } from '@angular/core';
import { Observer } from 'rxjs/Observer';
import { Observable } from 'rxjs/Observable';
import { Operator } from 'rxjs/Operator';
import { map } from 'rxjs/operator/map';
import { pluck } from 'rxjs/operator/pluck';
import { distinctUntilChanged } from 'rxjs/operator/distinctUntilChanged';
import { Action, ActionReducer } from './models';
import { ActionsSubject } from './actions_subject';
import { StateObservable } from './state';
import { ReducerManager } from './reducer_manager';
export class Store<T> extends Observable<T> implements Observer<Action> {
/**
 * @param {?} state$
 * @param {?} actionsObserver
 * @param {?} reducerManager
 */
constructor(
    state$: StateObservable,
private actionsObserver: ActionsSubject,
private reducerManager: ReducerManager
  ) {
    super();

    this.source = state$;
  }

  select<K>(mapFn: (state: T) => K): Store<K>;
  select<a extends keyof T>(key: a): Store<T[a]>;
  select<a extends keyof T, b extends keyof T[a]>(
    key1: a,
    key2: b
  ): Store<T[a][b]>;
  select<a extends keyof T, b extends keyof T[a], c extends keyof T[a][b]>(
    key1: a,
    key2: b,
    key3: c
  ): Store<T[a][b][c]>;
  select<
    a extends keyof T,
    b extends keyof T[a],
    c extends keyof T[a][b],
    d extends keyof T[a][b][c]
  >(key1: a, key2: b, key3: c, key4: d): Store<T[a][b][c][d]>;
  select<
    a extends keyof T,
    b extends keyof T[a],
    c extends keyof T[a][b],
    d extends keyof T[a][b][c],
    e extends keyof T[a][b][c][d]
  >(key1: a, key2: b, key3: c, key4: d, key5: e): Store<T[a][b][c][d][e]>;
  select<
    a extends keyof T,
    b extends keyof T[a],
    c extends keyof T[a][b],
    d extends keyof T[a][b][c],
    e extends keyof T[a][b][c][d],
    f extends keyof T[a][b][c][d][e]
  >(
    key1: a,
    key2: b,
    key3: c,
    key4: d,
    key5: e,
    key6: f
  ): Store<T[a][b][c][d][e][f]>;
/**
 * @param {?} pathOrMapFn
 * @param {...?} paths
 * @return {?}
 */
select(
    pathOrMapFn: ((state: T) => any) | string,
    ...paths: string[]
  ): Store<any> {
    let /** @type {?} */ mapped$: Store<any>;

    if (typeof pathOrMapFn === 'string') {
      mapped$ = pluck.call(this, pathOrMapFn, ...paths);
    } else if (typeof pathOrMapFn === 'function') {
      mapped$ = map.call(this, pathOrMapFn);
    } else {
      throw new TypeError(
        `Unexpected type '${typeof pathOrMapFn}' in select operator,` +
          ` expected 'string' or 'function'`
      );
    }

    return distinctUntilChanged.call(mapped$);
  }
/**
 * @template R
 * @param {?} operator
 * @return {?}
 */
lift<R>(operator: Operator<T, R>): Store<R> {
    const /** @type {?} */ store = new Store<R>(this, this.actionsObserver, this.reducerManager);
    store.operator = operator;

    return store;
  }
/**
 * @template V
 * @param {?} action
 * @return {?}
 */
dispatch<V extends Action = Action>(action: V) {
    this.actionsObserver.next(action);
  }
/**
 * @param {?} action
 * @return {?}
 */
next(action: Action) {
    this.actionsObserver.next(action);
  }
/**
 * @param {?} err
 * @return {?}
 */
error(err: any) {
    this.actionsObserver.error(err);
  }
/**
 * @return {?}
 */
complete() {
    this.actionsObserver.complete();
  }
/**
 * @template State, Actions
 * @param {?} key
 * @param {?} reducer
 * @return {?}
 */
addReducer<State, Actions extends Action = Action>(
    key: string,
    reducer: ActionReducer<State, Actions>
  ) {
    this.reducerManager.addReducer(key, reducer);
  }
/**
 * @template Key
 * @param {?} key
 * @return {?}
 */
removeReducer<Key extends keyof T>(key: Key) {
    this.reducerManager.removeReducer(key);
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: StateObservable, },
{type: ActionsSubject, },
{type: ReducerManager, },
];
}

function Store_tsickle_Closure_declarations() {
/** @type {?} */
Store.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Store.ctorParameters;
/** @type {?} */
Store.prototype.actionsObserver;
/** @type {?} */
Store.prototype.reducerManager;
}


export const /** @type {?} */ STORE_PROVIDERS: Provider[] = [Store];

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../modules/store/src/store.ts